// eFiling validation — per-tab and full-order validation
// Combines our rules with EFM requirements
import { isFeeQuoteResolved, mergeResults, paymentMethodRequired } from '../types.js';
import { caseCourtBinding } from '../common/court-identity.js';
import { validateParties, collectDocSelfRepPartyIds } from '../common/parties.js';
import { validateDocuments, isRepresentationChangeDoc } from '../common/documents.js';
import { feeWaiverSatisfied } from '../common/fee-waiver.js';
import { anyGuidedSectionUnconfirmed, sectionGateMessage, unconfirmedSectionsForStep, SECTIONS_UNCONFIRMED_RULE, SECTION_GATE_FIELD, SECTION_GATE_RULE, } from './section-gates.js';
import { getCaseCategoryFieldVisibility } from '../../efm-reference/case-category-metadata.js';
import { isUnlawfulDetainerCaseType } from '../../efm-reference/case-types.js';
import { isDefendantRole } from '../../efm-reference/party-roles.js';
// ---------------------------------------------------------------------------
// Shared validation inputs
// ---------------------------------------------------------------------------
/**
 * SPEC — single representation per filing (Madera clerk rule, 2026-06-29). A
 * document's FILING_PARTY set may name several parties, but they must share ONE
 * representation: one attorney of record (who may represent co-parties on the same
 * side) OR be self-represented. Naming parties under MULTIPLE representations in
 * one filing — e.g. a represented Petitioner alongside a self-rep Respondent, or
 * two different attorneys — is the cross-representation defect the court rejects
 * ("Court doesn't allow multiple attorneys representing multiple parties in one
 * eFiling"). This mirrors the canonical ECF `DocumentMetadataType`, which permits
 * unbounded `FilingPartyID` but exactly one `FilingAttorneyID`.
 *
 * Each filing party's representation bucket is computed the way the JTI envelope
 * resolves FILING_ATTORNEY: its attorney-of-record id if some attorney's
 * `representedPartyIds` includes the party, else `SELF`. All self-rep parties
 * collapse to one `SELF` bucket, so co-plaintiffs filing pro se (the accepted
 * multi-plaintiff shape, e.g. 26MA00006021) stay valid; >1 distinct bucket on one
 * document is the defect (the rejected 26MA00006019 / 26MA00006022).
 *
 * Boundary: two self-represented parties on OPPOSITE sides both bucket to `SELF`
 * and pass here — that combination is outside the known clerk rule (which is about
 * attorneys), so we do not speculatively reject it. The confirmed cross-side defect
 * always pairs a represented party with a different representation, which this
 * catches.
 */
export function validateFilingPartyRepresentation(state) {
    // local party id → its first linked attorney id (representation bucket key)
    const attorneyByParty = new Map();
    for (const att of state.attorneys ?? []) {
        for (const pid of att.representedPartyIds ?? []) {
            if (!attorneyByParty.has(pid))
                attorneyByParty.set(pid, att.id);
        }
    }
    const errors = [];
    for (const doc of state.documents ?? []) {
        const raw = doc.metadataValues?.FILING_PARTY;
        if (!Array.isArray(raw))
            continue;
        const partyIds = raw.filter((v) => typeof v === 'string');
        if (partyIds.length < 2)
            continue; // one party can't span representations
        const buckets = new Set(partyIds.map((id) => attorneyByParty.get(id) ?? 'SELF'));
        if (buckets.size > 1) {
            const label = doc.name || doc.fileName || doc.documentType?.name || 'A document';
            errors.push({
                field: 'documents',
                rule: 'filing-party-single-representation',
                message: `"${label}" names parties under more than one representation as the Filed By party. ` +
                    'A filing may name only one representation — one attorney (who may represent several parties on the same side), or self-represented parties. ' +
                    'Remove the parties that are not part of the filing party (e.g. the opposing side) so the Filed By party is a single representation.',
                source: 'ours',
            });
        }
    }
    return { valid: errors.length === 0, errors, warnings: [] };
}
// isRepresentationChangeDoc moved to ../common/documents.js (WOL-441 — the
// metadata validator there needs it, and common must not import from this
// module). Re-exported so the public order-logic surface is unchanged.
export { isRepresentationChangeDoc };
/**
 * SPEC — self-rep vs attorney-of-record consistency (Walter, 2026-07-02: the
 * rule is absolute, hard-block). A party the court roster shows as represented
 * — some attorney hydrated from GetCase (`externalId` set, the court-assigned
 * id) lists the party in `representedPartyIds` — cannot file self-represented
 * until a Substitution or Withdrawal of Attorney is filed. Emitting
 * `SELF_REP`/no-FILING_ATTORNEY for such a party contradicts the court record
 * and bounces at clerk review ("attorney rep instead of self-rep" — Madera
 * cert Sub #1 26MA00006073 / Sub #3 26MA00006076, 2026-07-01).
 *
 * The block lifts when the filing itself carries a Substitution/Withdrawal
 * document (envelope-level, matching the clerk's remedy — the representation
 * change rides along and is adjudicated first). Manually-added attorneys have
 * no `externalId` and never trigger the block: the court has no record of
 * them, so there is nothing to substitute away from.
 */
export function validateSelfRepAttorneyOfRecord(state) {
    const errors = [];
    const hasRepChangeDoc = (state.documents ?? []).some((doc) => isRepresentationChangeDoc(doc.documentType));
    if (hasRepChangeDoc)
        return { valid: true, errors, warnings: [] };
    for (const party of state.parties ?? []) {
        if (!party.isSelfRepresented)
            continue;
        const ofRecord = (state.attorneys ?? []).find((att) => !!att.externalId && (att.representedPartyIds ?? []).includes(party.id));
        if (!ofRecord)
            continue;
        const partyName = [party.firstName, party.lastName].filter(Boolean).join(' ')
            || party.organizationName
            || 'A party';
        const attorneyName = [ofRecord.firstName, ofRecord.lastName].filter(Boolean).join(' ');
        errors.push({
            field: 'parties',
            rule: 'self-rep-attorney-of-record',
            message: `${partyName} has an attorney of record${attorneyName ? ` (${attorneyName})` : ''} on this case. ` +
                'The court will not accept a self-represented filing for a represented party — ' +
                'file a Substitution of Attorney or Withdrawal of Attorney first (it can be included in this filing), ' +
                'or turn off self-represented for this party.',
            source: 'ours',
        });
    }
    return { valid: errors.length === 0, errors, warnings: [] };
}
/**
 * SPEC — FORMER_ATTORNEY must be court-emittable (SOR-151). A Substitution /
 * Withdrawal of Attorney names the outgoing attorney via the FORMER_ATTORNEY
 * metadata field. The submit/fee-calc envelope can only carry that attorney
 * when the roster entry holds the court-assigned participant id GetCase mints
 * (stage-3-envelope: `if (formerAttorney?.externalId)`). A hand-added attorney
 * (AddAttorneyModal) or an OCR-extracted one has none, yet it satisfies the
 * client's "value present" gate — so the selection sails past validation and is
 * then silently dropped from the envelope, and the court rejects the filing for
 * a missing former attorney the UI reported as complete (external tester,
 * 2026-07-18). The client's notion of "satisfied" and the envelope's notion of
 * "emittable" disagree, and nothing reconciles them.
 *
 * Fail fast instead: when a representation-change document carries a
 * FORMER_ATTORNEY selection that cannot be emitted (resolves to no roster
 * attorney, or to one with no court-assigned id), block the filer before
 * submission with a message that names the document and states the real problem
 * in filer language. Wired into `validateTab('documents')` and
 * `validateEFilingOrder` exactly like `validateSelfRepAttorneyOfRecord`, so it
 * flows through `validateFeeCalcReadiness` (the strict fee-calc/confirm gate)
 * on both client and server.
 *
 * Only fires when a value IS selected — an empty selection stays the existing
 * `metadata-required-FORMER_ATTORNEY` path, so the two never double-report.
 * Scoped to representation-change documents (`isRepresentationChangeDoc`), the
 * only docs FORMER_ATTORNEY lives on, so it can never arm on a step where the
 * field is not rendered (WOL-419 no-deadlock, as for the self-rep validator).
 * The selection read mirrors the envelope's (`getPartyIds`): an array of local
 * roster ids, tolerating a legacy bare-string value.
 */
export function validateFormerAttorneyEmittable(state) {
    const errors = [];
    const documents = state.documents ?? [];
    for (let i = 0; i < documents.length; i++) {
        const doc = documents[i];
        if (!isRepresentationChangeDoc(doc.documentType))
            continue;
        const raw = (doc.metadataValues ?? {}).FORMER_ATTORNEY;
        const selectedIds = Array.isArray(raw)
            ? raw.filter((v) => typeof v === 'string' && v.length > 0)
            : typeof raw === 'string' && raw.length > 0
                ? [raw]
                : [];
        // Nothing selected — the required-metadata path owns that case.
        if (selectedIds.length === 0)
            continue;
        // Emittable iff the selection resolves to a roster attorney carrying the
        // court-assigned participant id — the exact condition stage-3 emits on.
        const resolved = (state.attorneys ?? []).find((att) => selectedIds.includes(att.id));
        if (resolved?.externalId)
            continue;
        errors.push({
            field: `documents[${i}].metadataValues.FORMER_ATTORNEY`,
            rule: 'former-attorney-not-on-court-roster',
            message: `"${doc.name}" — the former attorney you selected is not on the court's ` +
                'case record for this case, so it cannot be used for this substitution. ' +
                "Select an attorney from the court's case record, or look up the case to " +
                "load the court's attorney list.",
            source: 'ours',
        });
    }
    return { valid: errors.length === 0, errors, warnings: [] };
}
/**
 * SPEC — unlawful-detainer defendant address (Walter R103, LegalConnect
 * parity, 2026-07-14). WHEN the case type is an unlawful detainer, every
 * defendant-side party must carry a mailing address (Address, City, State,
 * Zip) on its party record — LegalConnect's Edit-Party contact block is the
 * production reference. Satisfied by the party's `mailingAddress`, or by a
 * complete `selfRepAddress` when the defendant happens to be self-represented
 * (one address is enough; the filer is never asked twice).
 *
 * Merged into `validateTab('confirm')` only for now: the rule ships inside
 * the flag-gated confirm flow while the classic surface is frozen for the
 * 7/16 cert session. Fold it into 'case-details' + `validateEFilingOrder`
 * when the confirm flow becomes the default.
 */
export function validateUnlawfulDetainerDefendantAddress(state) {
    const errors = [];
    if (!isUnlawfulDetainerCaseType(state.caseInfo.caseType)) {
        return { valid: true, errors, warnings: [] };
    }
    const isComplete = (addr) => !!(addr?.address1 && addr?.city && addr?.state && addr?.zip);
    for (let i = 0; i < (state.parties ?? []).length; i++) {
        const party = state.parties[i];
        if (!isDefendantRole(party.roleCode))
            continue;
        if (isComplete(party.mailingAddress) || isComplete(party.selfRepAddress))
            continue;
        const partyName = [party.firstName, party.lastName].filter(Boolean).join(' ')
            || party.organizationName
            || 'The defendant';
        errors.push({
            field: `parties[${i}].mailingAddress`,
            rule: 'ud-defendant-address-required',
            message: `${partyName} must have a mailing address (Address, City, State, ZIP) — ` +
                'unlawful detainer filings require the defendant\'s mailing address on the party record.',
            source: 'ours',
        });
    }
    return { valid: errors.length === 0, errors, warnings: [] };
}
// ---------------------------------------------------------------------------
// Tab-level validators (our rules)
// ---------------------------------------------------------------------------
export function validateOrderInfo(orderInfo) {
    const errors = [];
    if (!orderInfo.county) {
        errors.push({
            field: 'orderInfo.county',
            rule: 'county-required',
            message: 'County is required',
            source: 'ours',
        });
    }
    if (!orderInfo.filingType) {
        errors.push({
            field: 'orderInfo.filingType',
            rule: 'filing-type-required',
            message: 'Filing type is required',
            source: 'ours',
        });
    }
    return { valid: errors.length === 0, errors, warnings: [] };
}
export function validateCaseInfo(caseInfo, filingType) {
    const errors = [];
    const isSubsequent = filingType === 'subsequent-filing';
    if (!caseInfo.caseTitle) {
        errors.push({
            field: 'caseInfo.caseTitle',
            rule: 'case-title-required',
            message: 'Case title is required',
            source: 'ours',
        });
    }
    if (!caseInfo.caseCategoryCode) {
        errors.push({
            field: 'caseInfo.caseCategoryCode',
            rule: 'case-category-required',
            message: 'Case category is required',
            source: 'ours',
        });
    }
    if (!caseInfo.caseType) {
        errors.push({
            field: 'caseInfo.caseType',
            rule: 'case-type-required',
            message: 'Case type is required',
            source: 'ours',
        });
    }
    // Subsequent filings skip incident zip and court jurisdiction — case already exists
    if (!isSubsequent && caseInfo.caseCategoryCode) {
        const visibility = getCaseCategoryFieldVisibility(caseInfo.caseCategoryCode);
        if (visibility.showCourtJurisdiction && !caseInfo.courtJurisdiction) {
            errors.push({
                field: 'caseInfo.courtJurisdiction',
                rule: 'court-jurisdiction-required',
                message: 'Court jurisdiction is required',
                source: 'ours',
            });
        }
        if (visibility.showIncidentZip && !caseInfo.incidentZip) {
            errors.push({
                field: 'caseInfo.incidentZip',
                rule: 'incident-zip-required',
                message: 'Incident zip is required',
                source: 'ours',
            });
        }
    }
    return { valid: errors.length === 0, errors, warnings: [] };
}
/**
 * Subsequent-filing case-linkage rules. Shared by the order-info tab validator
 * AND the whole-order validator (`validateEFilingOrder`) so the invariant holds
 * identically on the wizard, submit, and MCP readiness (`assessReadiness`) paths
 * — `validateOrderInfo` alone never sees `caseInfo`, so without this the submit /
 * MCP paths would let an un-attached subsequent filing through.
 *
 * Four rules, only for `subsequent-filing` (case-initiation creates the case, so
 * it is exempt):
 *   1. `case-attach-required` — the filing must bind to an existing court case.
 *      `caseTrackingId` is set ONLY by a successful case attach (the GetCase
 *      detail fetch in CaseSearch → `handleCaseSelected`); manually typing a case
 *      number sets `trialCourtCaseNumber` but never the JTI tracking id. Without
 *      this guard an un-attached subsequent filing reaches JTI and fails with an
 *      opaque "[99999] add at least one lead document" (cert Finding 9 — the
 *      empty-linkage envelope had no case to anchor the lead doc to).
 *   2. `subcase-required` — once attached, a multi-subcase case needs one subcase
 *      selected. Only meaningful after rule 1 is satisfied, hence `else if`.
 *   3. `case-county-mismatch` (SOR-154, SOR-182) — the attached case must belong
 *      to the currently selected court. `caseInfo.caseSourceCounty` holds the
 *      CaseCourtBinding: since SOR-182 the attach stamps it from the court the
 *      case record itself names, not from the county the lookup ran under, so a
 *      case that never belonged to the filing court is caught rather than
 *      matching by construction. Chained after rules 1/2 (only an attached case
 *      carries a binding). The classification is `caseCourtBinding` — the single
 *      canonical contract — so the comparison is between `courtIdentityKey` of
 *      the recorded value and `courtIdentityKey` of `orderInfo.county`, never
 *      the two raw strings: the record returns the JTI slug ("madera") while
 *      `orderInfo.county` holds the display name ("Madera"), and comparing those
 *      raw fires this hard error on every legitimate same-court filing. An
 *      absent binding grace-passes — we can't retroactively know its court.
 *      "Absent" includes a binding the draft cannot ATTRIBUTE to the case record
 *      (SOR-191): a pre-SOR-182 draft holds the filing-county echo, which
 *      canonicalizes equal to the filing court, so classifying it on the string
 *      alone would read `matches-court` and wave the repro through. In the
 *      CLASSIC flow it still grace-passes here — it just no longer asserts
 *      same-court. In the confirm flow rule 4 closes it.
 *   4. `case-binding-absent` (SPEC-SOR-174 R29) — CONFIRM FLOW ONLY: an
 *      attached case whose binding classifies `absent` fails closed instead of
 *      grace-passing, so the filer re-runs the lookup under the current court
 *      rather than filing on a court nothing ever asserted. Chained after rule
 *      3 on the same `caseCourtBinding` classification, so that mapping stays
 *      the single authority — empty, whitespace-only and unattributable
 *      recorded values are all `absent` there, and this rule never re-derives
 *      any of that from the raw string.
 *
 * `CaseCourtBindingState` has a FOURTH member, `unrecognized-court`, and no rule
 * here consumes it: a recorded court outside the county value space is "we
 * cannot place this", so rules 3 and 4 stay silent on it in both flows. That
 * silence is deliberate, not an omission — see `caseCourtBinding` for why a
 * classifier without it would have hard-blocked every subsequent filing in both
 * flows the moment a real court answered.
 *
 * Rules 3 and 4 split on flow, and the split follows the shipped code rather
 * than inventing a policy. This function takes no flow argument and rule 3 has
 * fired from `validateTab('order-info')` — the CLASSIC tab — since SOR-154, so
 * the foreign-court leg carries no flow guard: it pins behaviour the classic
 * flow already ships. An absent binding grace-passes in BOTH flows today, so
 * failing closed on it is genuinely new behaviour and is confirm-flow gated at
 * the site, exactly as every other new guided-section behaviour is (R16). The
 * classic flow therefore keeps its grace-pass and is tightened by nothing here.
 */
export function validateSubsequentCaseLinkage(orderInfo, caseInfo, opts = {}) {
    // requireSubcase defaults true (Confirm / submit enforce the full linkage).
    // The Upload/documents step passes false so a SELECTED multi-subcase case can
    // advance to Confirm, where the subcase picker renders and the subcase-required
    // gate fires (R95). Attaching the case satisfies the Upload gate;
    // case-attach-required still blocks an un-attached filing on Upload.
    //
    // confirmFlowEnabled defaults FALSE — the classic verdict. Rule 4 is the only
    // consumer, and a caller that has not opted in gets today's grace-pass, which
    // is what keeps every classic call site (and every pre-existing caller of this
    // function) behaving exactly as it did.
    const { requireSubcase = true, confirmFlowEnabled = false } = opts;
    const errors = [];
    if (orderInfo.filingType === 'subsequent-filing') {
        // Classified ONCE, by the one canonical comparator. Rules 3 and 4 are two
        // verdicts on the same binding state, so reading it twice would invite them
        // to drift apart — and neither may re-derive a binding state from the raw
        // recorded string (that is the whole point of `caseCourtBinding`). The
        // switch below is deliberately NOT exhaustive over the four states:
        // `unrecognized-court` and `matches-court` both fire nothing.
        const binding = caseCourtBinding(caseInfo, orderInfo.county);
        if (!caseInfo.caseTrackingId) {
            errors.push({
                field: 'caseInfo.caseTrackingId',
                rule: 'case-attach-required',
                message: 'Search for and select the existing case this filing belongs to before continuing.',
                source: 'ours',
            });
        }
        else if (requireSubcase && (caseInfo.subcaseCount ?? 0) > 1 && !caseInfo.complaintId) {
            errors.push({
                field: 'caseInfo.complaintId',
                rule: 'subcase-required',
                message: 'This case has multiple subcases. Select the subcase this filing belongs to before continuing.',
                source: 'ours',
            });
        }
        else if (binding === 'foreign-court') {
            errors.push({
                field: 'caseInfo.caseSourceCounty',
                rule: 'case-county-mismatch',
                message: 'The attached case belongs to a different county. Change the county back to that court or search for a case in the current county.',
                source: 'ours',
            });
        }
        else if (confirmFlowEnabled && binding === 'absent') {
            errors.push({
                field: 'caseInfo.caseSourceCounty',
                rule: 'case-binding-absent',
                message: 'We have no record of which court this case belongs to. Search for the case again in the current county to re-attach it.',
                source: 'ours',
            });
        }
    }
    return { valid: errors.length === 0, errors, warnings: [] };
}
export function validateCourtFees(courtFees, showCourtFeesTab) {
    // Generic guard: when a caller declares the court-fees tab hidden, its fees
    // are not validated. Every e-filing caller now passes `true` (SOR-155
    // removed the only path — Federal filings — that hid the tab).
    if (!showCourtFeesTab) {
        return { valid: true, errors: [], warnings: [] };
    }
    const errors = [];
    if (!courtFees.authorizePayment) {
        errors.push({
            field: 'courtFees.authorizePayment',
            rule: 'authorize-payment-required',
            message: 'Court fee payment must be authorized',
            source: 'ours',
        });
    }
    return { valid: errors.length === 0, errors, warnings: [] };
}
/**
 * The payment validator (SPEC-SOR-343) — the whole-order rule
 * (`validateEFilingOrder`) and, since SPEC-SOR-363, the portal's Submit-tab
 * stepper verdict both compose it: both of its rules key on the resolved fee
 * quote, which lives on `courtFees` and is invisible to a per-tab arm holding
 * only `orderDetails`, so the per-tab arm cannot host the rule and the portal
 * composes this function over both slices at its own verdict site instead. It
 * emits exactly one of two rules, never both: the two guards are negations of
 * each other. The card decision itself is `paymentMethodRequired` — this
 * function only pairs it with the field it names.
 */
export function validatePaymentMethod(courtFees, orderDetails) {
    if (!isFeeQuoteResolved(courtFees)) {
        return {
            valid: false,
            errors: [
                {
                    field: 'courtFees.quoteState',
                    rule: 'fee-quote-not-resolved',
                    message: 'Run the fee calculation before submitting.',
                    source: 'ours',
                },
            ],
            warnings: [],
        };
    }
    if (paymentMethodRequired(courtFees) && !orderDetails.paymentMethodId) {
        return {
            valid: false,
            errors: [
                {
                    field: 'orderDetails.paymentMethodId',
                    rule: 'payment-method-required',
                    message: 'Payment method is required',
                    source: 'ours',
                },
            ],
            warnings: [],
        };
    }
    return { valid: true, errors: [], warnings: [] };
}
// ---------------------------------------------------------------------------
// Tab validation dispatcher
// ---------------------------------------------------------------------------
/**
 * R10 — the unconfirmed-section blockers for one step, as ordinary tab rules.
 *
 * They are ordinary deliberately: the wizard's Next is already `!currentTabValid`
 * and its blocker panel already renders every `validateTab` error with a jump
 * target, so expressing the section gate as rules gates Next and names the
 * section in one move, with no second gating path to keep in agreement.
 *
 * `unconfirmedSectionsForStep` is the only source of the section list, so R21
 * is structural rather than a rule this function has to remember: the upload
 * half of the ownership map holds no parties or documents entry, and nothing
 * here can add one.
 */
function validateStepSections(state, step, options) {
    const errors = unconfirmedSectionsForStep(state, step).map((section) => ({
        field: SECTION_GATE_FIELD[section],
        rule: SECTION_GATE_RULE[section],
        message: sectionGateMessage(state, section, {
            metadataByDocType: options?.metadataByDocType,
            availableDocTypeCodes: options?.availableDocTypeCodes,
        }),
        source: 'ours',
    }));
    return { valid: errors.length === 0, errors, warnings: [] };
}
/**
 * Validate a specific wizard tab. Returns the same results as the inline
 * validation in EFilingContext.validateTab(), but as a structured result
 * instead of a boolean.
 */
export function validateTab(state, tab, options) {
    // Review Fees applies to every e-filing (SOR-155) — county selection does
    // not decide whether the step exists, only when it becomes reachable (R46).
    const showCourtFeesTab = options?.showCourtFeesTab ?? true;
    switch (tab) {
        case 'order-info':
            return mergeResults([
                validateOrderInfo(state.orderInfo),
                validateSubsequentCaseLinkage(state.orderInfo, state.caseInfo),
            ]);
        case 'case-details':
            return mergeResults([
                validateCaseInfo(state.caseInfo, state.orderInfo.filingType || undefined),
                validateParties({
                    parties: state.parties,
                    attorneys: state.attorneys,
                    docSelfRepPartyIds: collectDocSelfRepPartyIds(state.documents),
                }),
            ]);
        case 'confirm':
            // Consolidated "Confirm your filing" page (Walter R49/R64 phase 1): the
            // structural merge of Where to File + About Your Case, so it validates
            // as exactly the union of the two steps it replaces — plus the
            // confirm-flow-only R103 UD defendant-address rule (classic stays
            // untouched while frozen; see the validator's SPEC comment).
            return mergeResults([
                validateOrderInfo(state.orderInfo),
                // R29 — the Confirm step's Next refuses an absent binding. `true` rather
                // than `options?.confirmFlowEnabled`: this tab is rendered only by
                // CONFIRM_TABS, so reaching this case IS the confirm flow, and a
                // fail-closed protection must not depend on a caller remembering to opt
                // in. (The R10 section line below still defers to the option — the
                // guided-section MODEL is inert unless a caller opts in, R16.)
                validateSubsequentCaseLinkage(state.orderInfo, state.caseInfo, { confirmFlowEnabled: true }),
                validateCaseInfo(state.caseInfo, state.orderInfo.filingType || undefined),
                validateParties({
                    parties: state.parties,
                    attorneys: state.attorneys,
                    docSelfRepPartyIds: collectDocSelfRepPartyIds(state.documents),
                }),
                validateUnlawfulDetainerDefendantAddress(state),
                // R10 — the Confirm step owns the parties and documents sections
                // (AX3), so its Next waits on the filer confirming both.
                options?.confirmFlowEnabled
                    ? validateStepSections(state, 'confirm', {
                        metadataByDocType: options?.metadataByDocType,
                        availableDocTypeCodes: options?.availableDocTypeCodes,
                    })
                    : { valid: true, errors: [], warnings: [] },
            ]);
        case 'documents': {
            // Build formGroupsByDocType from documents that have formGroups stored
            const formGroupsByDocType = {};
            for (const doc of state.documents) {
                if (doc.documentType && doc.formGroups) {
                    formGroupsByDocType[doc.documentType.code] = doc.formGroups;
                }
            }
            // R90 (WOL-442): with the foundation gate on, doc-type deferral is
            // overridden for non-subsequent filings — an untyped document is a hard
            // error on Upload even when the doc-type list hasn't loaded yet (the
            // filer unlocks it by setting court + case type in place, strictly
            // forward). Subsequent filings keep the deferral (their types unlock
            // after the case lookup on the Confirm step).
            const foundationGate = (options?.requireUploadFoundation ?? false) &&
                state.orderInfo.filingType !== 'subsequent-filing';
            const docResult = validateDocuments(state.documents, {
                deferPartyAssignment: options?.deferPartyAssignment,
                deferDocTypeAssignment: foundationGate ? false : options?.deferDocTypeAssignment,
                formGroupsByDocType: Object.keys(formGroupsByDocType).length > 0 ? formGroupsByDocType : undefined,
                filingType: state.orderInfo.filingType || undefined,
                metadataByDocType: options?.metadataByDocType,
                // R18: a self-rep filing party satisfies the doc-level FILING_ATTORNEY
                // requirement (the envelope emits FILING_PARTY_ADDRESS for it instead).
                selfRepPartyIds: state.parties.filter(p => p.isSelfRepresented).map(p => p.id),
            });
            const feeWaiverErrors = [];
            // Delegate to the shared feeWaiverSatisfied predicate so the submit gate
            // can never disagree with the fee-calc emitter or completeness checklist.
            // FW-001 detection stays code-first (feeWaiverSatisfied → isFeeWaiverRequestDoc).
            // A FEE_WAIVER party is satisfied without FW-001 when feeWaiverOnFile === true
            // (existing-waiver carve-out); GOVT_ENTITY (and NONE) never require FW-001.
            const feeWaiverBlocked = state.parties.some((party) => !feeWaiverSatisfied(party, state.documents));
            if (feeWaiverBlocked) {
                feeWaiverErrors.push({
                    field: 'documents',
                    rule: 'fee-waiver-requires-fw001',
                    message: 'Fee waiver requires a Request to Waive Court Fees document. Upload it before submitting.',
                    source: 'ours',
                });
            }
            // R90 (WOL-442): the Upload foundation — court, and (for new cases)
            // case category & type. Every one of these is settable ON the Upload
            // step (in-card selectors), so hard-requiring them here cannot deadlock
            // (WOL-419 invariant holds).
            const foundationErrors = [];
            if (options?.requireUploadFoundation) {
                if (!state.orderInfo.state) {
                    foundationErrors.push({
                        field: 'orderInfo.state',
                        rule: 'upload-court-required',
                        message: 'Select the filing state in the Court card',
                        source: 'ours',
                    });
                }
                if (!state.orderInfo.county) {
                    foundationErrors.push({
                        field: 'orderInfo.county',
                        rule: 'upload-court-required',
                        message: 'Select the filing county in the Court card',
                        source: 'ours',
                    });
                }
                if (foundationGate) {
                    if (!state.caseInfo.caseCategoryCode) {
                        foundationErrors.push({
                            field: 'caseInfo.caseCategoryCode',
                            rule: 'upload-case-category-required',
                            message: 'Select the case category in the Case Category & Type card',
                            source: 'ours',
                        });
                    }
                    if (!state.caseInfo.caseType?.code) {
                        foundationErrors.push({
                            field: 'caseInfo.caseType',
                            rule: 'upload-case-type-required',
                            message: 'Select the case type in the Case Category & Type card',
                            source: 'ours',
                        });
                    }
                }
            }
            return mergeResults([
                docResult,
                // A subsequent filing must bind to an existing court case (caseTrackingId)
                // before Next unlocks on the Upload step — the new→existing mode switch
                // (FilingTypeDetection "This isn't a new case →") lands the filer here
                // with an empty linkage, so without this the wizard let Next through with
                // no case attached (SOR-156). Additive; case-initiation is exempt.
                // requireSubcase:false — a multi-subcase case need only be ATTACHED to
                // pass Upload; its subcase is picked on Confirm, where the picker renders
                // and subcase-required fires (R95). Single-subcase filings are unaffected.
                // R29 — the Upload step is rendered by BOTH flows (CLASSIC_TABS and
                // CONFIRM_TABS each carry it), so unlike the confirm tab above this one
                // must take the flow from the caller: threading the option is the only
                // thing keeping the classic Upload Next on today's grace-pass.
                validateSubsequentCaseLinkage(state.orderInfo, state.caseInfo, {
                    requireSubcase: false,
                    confirmFlowEnabled: options?.confirmFlowEnabled,
                }),
                { valid: foundationErrors.length === 0, errors: foundationErrors, warnings: [] },
                // R10 — the Upload step owns the court and case sections (AX3), so its
                // Next waits on the filer confirming both. R21 holds by construction:
                // the section list comes from the upload half of the ownership map, so
                // no argument to this call can produce a blocker naming the parties or
                // documents section — the WOL-419 no-deadlock rule.
                options?.confirmFlowEnabled
                    ? validateStepSections(state, 'upload', {
                        metadataByDocType: options?.metadataByDocType,
                        availableDocTypeCodes: options?.availableDocTypeCodes,
                    })
                    : { valid: true, errors: [], warnings: [] },
                { valid: feeWaiverErrors.length === 0, errors: feeWaiverErrors, warnings: [] },
                validateFilingPartyRepresentation(state),
                validateSelfRepAttorneyOfRecord(state),
                validateFormerAttorneyEmittable(state),
            ]);
        }
        case 'court-fees':
            return validateCourtFees(state.courtFees, showCourtFeesTab);
        case 'order-details':
            // SPEC-SOR-343 R19 cutover: the whole-order validator is the only
            // decider of the payment requirement, and both of its rules read the
            // resolved fee quote on `courtFees` — a fact a per-tab arm holding only
            // `orderDetails` cannot see, so re-keying the arm here would be a second
            // decider over data it does not have. The arm stays so the dispatcher
            // still answers the tab; it emits no payment rule for any state.
            return { valid: true, errors: [], warnings: [] };
        default:
            return { valid: false, errors: [{ field: 'tab', rule: 'unknown-tab', message: `Unknown tab: ${tab}`, source: 'ours' }], warnings: [] };
    }
}
// ---------------------------------------------------------------------------
// Document-first deferral predicates + fee-calc readiness (Walter 2026-07-10)
// ---------------------------------------------------------------------------
/**
 * True when the wizard state can load a document-type list — the same
 * precondition the Documents step's reference fetch keys on (category name +
 * resolved case-type code + filing type). While false, a missing document type
 * stays a deferred warning (the document-first Upload step precedes the case
 * info that unlocks the list); the moment this turns true, a missing document
 * type must block navigation (R5 — a type-less document that sails past the
 * Upload step surfaces later as the opaque JTI fee-calc fault
 * "4112: Invalid document identification").
 */
export function canAssignDocTypes(state) {
    return !!(state.caseInfo.caseCategoryName &&
        state.caseInfo.caseType?.code &&
        state.orderInfo.filingType);
}
/**
 * True when party-class document metadata (FILING_PARTY, PARTY_SERVED, …) can
 * actually be assigned — a roster exists to reference. While the roster is
 * empty (cold-start upload before extraction / case attach), party-class
 * requirements stay deferred warnings; once parties exist they are hard
 * errors (R6).
 */
export function canAssignPartyMetadata(state) {
    return state.parties.length > 0;
}
/**
 * Strict envelope readiness for fee calculation (R7): the full documents-tab
 * rule set with NO document-first deferrals. The wizard gates entering the
 * Review Fees step on this, and CourtFeesTab refuses to initiate fee-calc
 * while it fails — an envelope with a type-less document otherwise reaches the
 * EFM and bounces as a raw transport fault that names neither the document nor
 * the fix.
 */
export function validateFeeCalcReadiness(state, options) {
    return validateTab(state, 'documents', {
        metadataByDocType: options?.metadataByDocType,
    });
}
// ---------------------------------------------------------------------------
// Full order validation
// ---------------------------------------------------------------------------
/**
 * R30 — the validator's own refusal of an unconfirmed confirm-flow draft.
 *
 * This is the half of "the client gate and the validator agree" that actually
 * bites. Without it the agreement is empty in the direction that matters: the
 * validator would report a fully-prefilled, zero-confirmation draft VALID, and
 * the server-side readiness path (`assessReadiness` → `getValidationStatus` /
 * `markReadyForReview`) would mark it ready for review behind the client gate's
 * back — a filing nobody approved, queued for a human reviewer as though
 * someone had.
 *
 * Keyed on the draft's own `confirmFlowActive`, not on a call-site option: the
 * server callers pass state and nothing else, and a rule reachable only by
 * opting in is exactly the rule the readiness path would miss.
 */
function validateGuidedSectionsConfirmed(state) {
    if (!state.confirmFlowActive || !anyGuidedSectionUnconfirmed(state)) {
        return { valid: true, errors: [], warnings: [] };
    }
    return {
        valid: false,
        errors: [
            {
                field: 'sectionStatuses',
                rule: SECTIONS_UNCONFIRMED_RULE,
                message: 'Confirm every section of the filing before submitting.',
                source: 'ours',
            },
        ],
        warnings: [],
    };
}
/**
 * Validate the entire eFiling order (our rules only).
 * Runs all tab validators and merges results.
 */
export function validateEFilingOrder(state) {
    // Court fees always apply to an e-filing order (SOR-155 removed the Federal path).
    const showCourtFeesTab = true;
    // Build formGroupsByDocType from documents
    const formGroupsByDocType = {};
    for (const doc of state.documents) {
        if (doc.documentType && doc.formGroups) {
            formGroupsByDocType[doc.documentType.code] = doc.formGroups;
        }
    }
    return mergeResults([
        validateOrderInfo(state.orderInfo),
        // R29 — keyed on the draft's own `confirmFlowActive`, for the same reason
        // `validateGuidedSectionsConfirmed` is (below): the server readiness path
        // (`assessReadiness` → `getValidationStatus` / `markReadyForReview`) passes
        // state and nothing else, so a rule reachable only by a call-site option is
        // exactly the rule that path would miss.
        validateSubsequentCaseLinkage(state.orderInfo, state.caseInfo, {
            confirmFlowEnabled: !!state.confirmFlowActive,
        }),
        validateCaseInfo(state.caseInfo, state.orderInfo.filingType || undefined),
        validateParties({
            parties: state.parties,
            attorneys: state.attorneys,
            docSelfRepPartyIds: collectDocSelfRepPartyIds(state.documents),
        }),
        validateDocuments(state.documents, {
            formGroupsByDocType: Object.keys(formGroupsByDocType).length > 0 ? formGroupsByDocType : undefined,
            filingType: state.orderInfo.filingType || undefined,
        }),
        validateFilingPartyRepresentation(state),
        validateSelfRepAttorneyOfRecord(state),
        validateFormerAttorneyEmittable(state),
        validateCourtFees(state.courtFees, showCourtFeesTab),
        validatePaymentMethod(state.courtFees, state.orderDetails),
        validateGuidedSectionsConfirmed(state),
    ]);
}
/**
 * Validate the entire eFiling order including EFM-specific requirements.
 * Two-layer validation: our rules + EFM pre-flight check.
 */
export function validateOrder(state, efm) {
    // Layer 1: Our rules
    const ourResult = validateEFilingOrder(state);
    // Layer 2: EFM-specific requirements
    const efmResult = validateForEfm(state, efm);
    return mergeResults([ourResult, efmResult]);
}
/**
 * EFM-specific pre-flight validation.
 * Checks EFM submission requirements that go beyond our base rules.
 */
export function validateForEfm(state, efm) {
    const errors = [];
    const requirements = efm.getSubmissionRequirements(state);
    // Check lead document requirement
    if (requirements.requireLeadDocument) {
        const hasLeadDoc = state.documents.some(d => d.isLeadDocument);
        if (!hasLeadDoc && state.documents.length > 0) {
            errors.push({
                field: 'documents',
                rule: 'efm-lead-document-required',
                message: `${efm.name} requires a lead document to be designated`,
                source: 'efm',
            });
        }
    }
    // Check allowed MIME types
    if (requirements.allowedMimeTypes) {
        const allowed = requirements.allowedMimeTypes;
        for (let i = 0; i < state.documents.length; i++) {
            const doc = state.documents[i];
            if (!allowed.includes(doc.mimeType)) {
                errors.push({
                    field: `documents[${i}].mimeType`,
                    rule: 'efm-mime-type',
                    message: `${efm.name} does not accept ${doc.mimeType} files. Allowed: ${allowed.join(', ')}`,
                    source: 'efm',
                });
            }
        }
    }
    // Check max file size
    if (requirements.maxFileSize) {
        const max = requirements.maxFileSize;
        for (let i = 0; i < state.documents.length; i++) {
            const doc = state.documents[i];
            if (doc.size > max) {
                errors.push({
                    field: `documents[${i}].size`,
                    rule: 'efm-max-file-size',
                    message: `Document "${doc.name}" exceeds maximum file size of ${Math.round(max / 1024 / 1024)}MB`,
                    source: 'efm',
                });
            }
        }
    }
    // A captured NEW_ATTORNEY (Substitution / Association of Attorney) must carry
    // the incoming attorney's contact info. JTI's CMS accepts a name-and-bar-only
    // envelope but the cert reviewer flags it "missing new atty contact info"
    // (Brenda, 26MA00005150 / Sub-1). The JTI envelope builder only emits the
    // <ContactInformation> block when email or a mailing address is present, so
    // require at least one contact channel before submit.
    for (let i = 0; i < state.documents.length; i++) {
        const raw = state.documents[i].metadataValues?.NEW_ATTORNEY;
        const atty = (Array.isArray(raw) ? raw[0] : raw);
        const captured = !!(atty && (atty.firstName || atty.lastName || atty.barNumber));
        const hasContact = !!(atty && (atty.email || atty.address?.address1));
        if (captured && !hasContact) {
            errors.push({
                field: `documents[${i}].metadataValues.NEW_ATTORNEY`,
                rule: 'efm-new-attorney-contact-required',
                message: `New attorney on "${state.documents[i].name}" is missing contact info — add an email or mailing address`,
                source: 'efm',
            });
        }
    }
    return { valid: errors.length === 0, errors, warnings: [] };
}
